import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, ProgressBar, Alert } from 'react-bootstrap';
import { FaKey, FaClock, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import LicenseEntryModal from './LicenseEntryModal';

const LicenseStatus = ({ showDetailed = false }) => {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLicenseStatus();
  }, []);

  const fetchLicenseStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/license/status', {
        headers: { 'x-auth-token': token }
      });
      setLicenseStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching license status:', err);
      setError('Unable to check license status');
    } finally {
      setLoading(false);
    }
  };

  const handleLicenseUpdated = () => {
    setShowLicenseModal(false);
    fetchLicenseStatus();
  };

  const getStatusBadge = () => {
    if (!licenseStatus) return <Badge bg="secondary">Unknown</Badge>;
    
    switch (licenseStatus.status) {
      case 'ACTIVE':
        return <Badge bg="success"><FaCheckCircle className="me-1" />Active</Badge>;
      case 'EXPIRED':
        return <Badge bg="danger"><FaExclamationTriangle className="me-1" />Expired</Badge>;
      case 'NO_LICENSE':
        return <Badge bg="warning"><FaKey className="me-1" />No License</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getProgressVariant = () => {
    if (!licenseStatus || !licenseStatus.daysRemaining) return 'secondary';
    
    if (licenseStatus.daysRemaining <= 3) return 'danger';
    if (licenseStatus.daysRemaining <= 7) return 'warning';
    if (licenseStatus.daysRemaining <= 14) return 'info';
    return 'success';
  };

  const getProgressPercentage = () => {
    if (!licenseStatus || !licenseStatus.daysRemaining) return 0;
    
    const totalDays = licenseStatus.licenseType === 'TRIAL' ? 60 : 180;
    return Math.max(0, (licenseStatus.daysRemaining / totalDays) * 100);
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <Card.Body>
          <div className="text-center">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Checking license status...
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-3">
        <FaExclamationTriangle className="me-2" />
        {error}
        <Button variant="link" size="sm" onClick={fetchLicenseStatus} className="ms-2">
          Retry
        </Button>
      </Alert>
    );
  }

  if (!showDetailed && licenseStatus?.isValid) {
    // Compact view for valid licenses
    return (
      <div className="d-flex align-items-center mb-2">
        {getStatusBadge()}
        {licenseStatus.daysRemaining <= 14 && (
          <small className="text-muted ms-2">
            <FaClock className="me-1" />
            {licenseStatus.daysRemaining} days remaining
          </small>
        )}
      </div>
    );
  }

  return (
    <>
      <Card className={`mb-3 ${!licenseStatus?.isValid ? 'border-warning' : ''}`}>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <FaKey className="me-2" />
            License Status
          </h6>
          {getStatusBadge()}
        </Card.Header>
        <Card.Body>
          {licenseStatus?.isValid ? (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>License Type:</strong>
                  <div className="text-muted">
                    {licenseStatus.licenseType === 'TRIAL' ? 'Trial Version' : 'Standard License'}
                  </div>
                </div>
                <div className="col-md-6">
                  <strong>Expires:</strong>
                  <div className="text-muted">
                    {new Date(licenseStatus.expirationDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small><strong>Days Remaining</strong></small>
                  <small>{licenseStatus.daysRemaining} days</small>
                </div>
                <ProgressBar 
                  now={getProgressPercentage()} 
                  variant={getProgressVariant()}
                  style={{ height: '8px' }}
                />
              </div>
              
              {licenseStatus.warningNeeded && (
                <Alert variant="warning" className="mb-3">
                  <FaExclamationTriangle className="me-2" />
                  <strong>License Expiring Soon!</strong>
                  <br />
                  Your license will expire in {licenseStatus.daysRemaining} day{licenseStatus.daysRemaining !== 1 ? 's' : ''}. 
                  Please contact support to renew your license.
                </Alert>
              )}
            </div>
          ) : (
            <div>
              {licenseStatus?.status === 'EXPIRED' ? (
                <Alert variant="danger">
                  <FaExclamationTriangle className="me-2" />
                  <strong>License Expired</strong>
                  <br />
                  Your license expired on {new Date(licenseStatus.expirationDate).toLocaleDateString()}. 
                  The application is now in read-only mode.
                </Alert>
              ) : (
                <Alert variant="warning">
                  <FaKey className="me-2" />
                  <strong>No License Found</strong>
                  <br />
                  Please enter a valid license key to activate the application.
                </Alert>
              )}
            </div>
          )}
          
          <div className="d-flex gap-2">
            <Button 
              variant={licenseStatus?.isValid ? "outline-primary" : "primary"}
              size="sm"
              onClick={() => setShowLicenseModal(true)}
            >
              <FaKey className="me-1" />
              {licenseStatus?.isValid ? 'Update License' : 'Enter License Key'}
            </Button>
            
            <Button 
              variant="outline-secondary"
              size="sm"
              onClick={fetchLicenseStatus}
            >
              Refresh Status
            </Button>
          </div>
        </Card.Body>
      </Card>
      
      <LicenseEntryModal 
        show={showLicenseModal}
        onHide={() => setShowLicenseModal(false)}
        onLicenseUpdated={handleLicenseUpdated}
        currentStatus={licenseStatus}
      />
    </>
  );
};

export default LicenseStatus;
